import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BellIcon } from "@/utilities/icon-utils";
import { cn } from "@/utilities/tailwind-utils";
import { useUserTypeFromPath } from "@/hooks";
import { useRoleNotifications } from "@/hooks/use-role-notifications";
import {
  formatNotificationTimestamp,
  getNotificationBadgeVariant,
  getNotificationIcon,
  type UiNotification
} from "@/utilities/notification-view";

export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const userType = useUserTypeFromPath();
  const {
    notifications,
    unreadCount,
    isLoading,
    supportsApi,
    markNotificationRead,
    markAllNotificationsRead
  } = useRoleNotifications({ pageSize: 8 });

  const handleNotificationClick = useCallback(async (notification: UiNotification) => {
    if (!notification.read) {
      try {
        await markNotificationRead(notification.id);
      } catch {
        /* toast optional */
      }
    }

    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  }, [markNotificationRead, navigate]);

  const onMarkAllAsRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
    } catch {
      /* optional */
    }
  }, [markAllNotificationsRead]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative"
          title="Notifications"
        >
          <BellIcon className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-sm">Notifications</h3>
          {supportsApi && unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void onMarkAllAsRead()}
              className="text-xs"
            >
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Loading…
            </div>
          ) : !supportsApi ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Notifications are available for practice and professional
              accounts.
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No notifications
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    "p-4 hover:bg-accent cursor-pointer transition-colors",
                    !notification.read && "bg-accent/50"
                  )}
                  onClick={() => void handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-lg flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm truncate">
                          {notification.title}
                        </h4>
                        <Badge
                          variant={getNotificationBadgeVariant(
                            notification.type
                          )}
                          className="ml-2 text-xs"
                        >
                          {notification.type}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {formatNotificationTimestamp(notification.timestamp)}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {supportsApi && notifications.length > 0 && (
          <div className="p-4 border-t">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                setOpen(false);
                navigate(`/${userType}/notifications`);
              }}
            >
              View all notifications
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
